/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Skeleton } from '@coursera/cds-core';

const styles = {
  shimmerContainer: css`
    .shimmer {
      display: flex;
      gap: 20px;
      transition: all 1s ease;
    }

    .card {
      background: var(--cds-color-emphasis-neutral-background-xweak);
      flex: 1;
      height: 500px;
      opacity: 1;
      transition: all 1s ease;
      padding: 36px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      border-radius: 8px;
    }

    /* Stage 1: fade out two boxes and expand the others */
    .stage-1 #card2,
    .stage-1 #card4 {
      animation: fadeOut 1s forwards;
    }

    .stage-1 {
      gap: 10px;
    }

    @keyframes fadeOut {
      0% {
        opacity: 1;
        flex: 1;
      }

      100% {
        opacity: 0;
        flex: 0;
        height: 0;
        padding: 0;
      }
    }

    /* Stage 2: fade out one more box */
    .stage-2 #card1 {
      animation: fadeOut3 1s forwards;
    }

    .stage-2 .placeholder1 {
      animation: shrink 1s forwards;
    }

    .stage-2 {
      gap: 0;
    }

    .stage-2 #card2,
    .stage-2 #card4 {
      height: 0;
      opacity: 0;
      padding: 0;
      flex: 0;
    }

    @keyframes shrink {
      0% {
        width: 100%;
      }

      100% {
        width: 30%;
      }
    }

    @keyframes fadeOut3 {
      0% {
        opacity: 1;
        flex: 1;
      }

      100% {
        opacity: 0;
        flex: 0;
        height: 0;
        padding: 0;
      }
    }

    /* Stage 3: change placeholder sizes */
    .stage-3 .placeholder1 {
      animation: shrink1 1s forwards;
    }

    .stage-3 .placeholder2 {
      animation: shrink2 1s forwards;
    }

    .stage-3 .placeholder3 {
      width: 30%;
      animation: expand4 1s forwards;
    }

    .stage-3 #card3 {
      animation: shrink3 1s forwards;
    }

    .stage-3 {
      gap: 0;
    }

    .stage-3 #card2,
    .stage-3 #card4,
    .stage-3 #card1 {
      height: 0;
      opacity: 0;
      padding: 0;
      flex: 0;

      span {
        display: none;
      }
    }

    @keyframes shrink1 {
      0% {
        width: 30%;
      }

      100% {
        width: 36px;
        height: 36px;
      }
    }

    @keyframes shrink2 {
      0% {
        width: 80%;
      }

      100% {
        width: 20%;
      }
    }

    @keyframes shrink3 {
      0% {
        height: 500px;
      }

      100% {
        height: 400px;
      }
    }

    @keyframes expand4 {
      0% {
        width: 70%;
      }

      100% {
        width: 30%;
        height: 96px;
      }
    }
  `,
};

type Props = {
  stage: number;
};

const ShimmerOneCard = ({ stage }: Props) => {
  return (
    <div css={styles.shimmerContainer}>
      <div className={`shimmer stage-${stage}`}>
        {[1, 2, 3, 4].map((i) => (
          <div className="card" id={`card${i}`} key={i}>
            <Skeleton className="placeholder1" variant="rect" width="100%" height={230} />
            <Skeleton className="placeholder2" variant="rect" width="80%" height={64} />
            <Skeleton className="placeholder3" variant="rect" width="70%" height={32} />
            <Skeleton className="placeholder4" variant="rect" width="20%" height={36} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ShimmerOneCard;
